<template>
  <MobilePage ref="pageRef" :locked="route.name === 'pwa-company-detail'">
    <!-- Hidden (but keeps its height and scroll position) while a company is open, so it can't show through -->
    <div class="p-4 space-y-4" :class="{ invisible: route.name === 'pwa-company-detail' }">
      <div class="grid grid-cols-2 p-1 rounded-md bg-gray-200 text-sm">
        <button
          v-for="option in views"
          :key="option.key"
          class="py-1.5 rounded"
          :class="view === option.key ? 'bg-white shadow text-dark-primary font-semibold' : 'text-gray-500'"
          @click="view = option.key"
        >{{ option.label }}</button>
      </div>

      <!-- A form so the keyboard's Search/Go key also submits -->
      <form class="space-y-2" @submit.prevent="search">
        <div class="relative">
          <input
            v-model="filters.companyName"
            type="search"
            placeholder="Company name"
            class="w-full appearance-none bg-white rounded-md shadow-md border-none py-2 pl-3 pr-9 leading-5 text-xs text-gray-900 focus:ring-0"
          />
          <button v-if="filters.companyName" type="button" class="absolute inset-y-0 right-0 flex items-center px-3" @click="filters.companyName = ''">
            <XMarkIcon class="h-4 w-4 text-gray-400" />
          </button>
        </div>
        <BaseComboBox v-model="filters.campaign" :options="options.campaign" placeholder="Campaign" singleValue nullable clearable roundedClass="rounded-md" :page-size="20" />
        <BaseComboBox v-model="filters.collectionTag" :options="options.collectionTag" placeholder="Collection tag" singleValue nullable clearable roundedClass="rounded-md" :page-size="20" />
        <BaseComboBox v-model="filters.source" :options="options.source" placeholder="Source" singleValue nullable clearable roundedClass="rounded-md" :page-size="20" />
        <div class="flex items-center justify-between">
          <div v-if="view === 'all'" class="flex items-center gap-2">
            <button
              type="button"
              class="px-3 py-2 rounded-md border border-dark-primary/30 bg-white text-sm text-dark-primary disabled:opacity-50"
              :disabled="isSavingAll || companies.length === 0 || !isOnline"
              @click="saveAll"
            >{{ isSavingAll ? 'Saving…' : 'Save All' }}</button>
            <span class="text-xs text-gray-500">{{ totalCount }}{{ isCountCapped ? '+' : '' }}</span>
          </div>
          <div v-else class="flex items-center gap-2">
            <button
              type="button"
              class="px-3 py-2 rounded-md border border-dark-primary/30 bg-white text-sm text-dark-primary disabled:opacity-50"
              :disabled="filteredSavedCompanies.length === 0"
              @click="unsaveAll"
            >Unsave All</button>
            <span class="text-xs text-gray-500">{{ filteredSavedCompanies.length }}</span>
          </div>
          <div class="flex items-center gap-4">
            <button type="button" class="text-sm text-accent-primary" @click="clearFilters">Clear</button>
            <button type="submit" class="px-4 py-2 rounded-md bg-dark-primary text-white">Search</button>
          </div>
        </div>
      </form>

      <div v-if="view === 'saved'" class="space-y-3">
        <div v-if="savedCompanies.length === 0" class="text-center text-gray-500 py-8">No saved companies</div>
        <div v-else-if="filteredSavedCompanies.length === 0" class="text-center text-gray-500 py-8">No companies found</div>
        <MobileCompanyCard
          v-for="company in filteredSavedCompanies"
          :key="company.id"
          :company="company"
          saved
          :busy="savingIds.has(company.id)"
          :disabled="isSavingAll"
          class="cursor-pointer"
          @toggle-save="toggleSaved(company)"
          @click="openCompany(company)"
        />
      </div>

      <template v-else>
      <div v-if="!isLoading && companies.length === 0" class="text-center text-gray-500 py-8">No companies found</div>

      <div class="space-y-3">
        <template v-if="isLoading">
          <MobileCompanyCardSkeleton v-for="n in 4" :key="n" />
        </template>
        <template v-else>
          <MobileCompanyCard
            v-for="company in companies"
            :key="company.id"
            :company="company"
            :saved="savedIds.has(company.id)"
            :busy="savingIds.has(company.id)"
            :disabled="isSavingAll"
            class="cursor-pointer"
            @toggle-save="toggleSaved(company)"
            @click="openCompany(company)"
          />
        </template>
      </div>

      <div v-if="page > 1 || hasMore" class="flex items-center justify-center gap-6 pt-1 text-sm">
        <button class="px-3 py-2 rounded-md bg-white shadow text-dark-primary disabled:opacity-40" :disabled="isLoading || page <= 1" @click="changePage(page - 1)">‹ Prev</button>
        <div class="text-center text-xs text-gray-500">
          <div>Page {{ page }} of {{ numPages }}{{ isCountCapped ? '+' : '' }}</div>
          <div>{{ totalCount }}{{ isCountCapped ? '+' : '' }} companies</div>
        </div>
        <button class="px-3 py-2 rounded-md bg-white shadow text-dark-primary disabled:opacity-40" :disabled="isLoading || !hasMore" @click="changePage(page + 1)">Next ›</button>
      </div>
      </template>
    </div>
    <router-view v-slot="{ Component }">
      <!-- Kept alive so switching tabs and back returns to the same detail and scroll position -->
      <div v-show="route.name === 'pwa-company-detail'" class="fixed inset-0 z-40 bg-gray-100">
        <keep-alive :max="1" :key="detailCacheKey">
          <component v-if="Component && route.name === 'pwa-company-detail'" :is="Component" :key="route.params.companyId" />
        </keep-alive>
      </div>
    </router-view>
  </MobilePage>
</template>

<script setup>
import { ref, reactive, computed, watch, onActivated } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useOnline } from '@vueuse/core';
import { getPWACompanies } from '@/services/Company';
import { getMinimalActiveCampaigns } from '@/services/Campaign';
import { getMinimalCollectionTags } from '@/services/CollectionTag';
import { getSources } from '@/services/Source';
import { getSavedCompanies, saveCompany, saveCompanies, unsaveCompany, unsaveCompanies, MAX_SAVED_COMPANIES } from '@/utils/savedCompanies';
import { logSavedCompanies, NOT_LOGGED_MESSAGE } from '@/components/mobile/company/savedCompanyLog';
import { useToast } from 'vue-toast-notification';
import BaseComboBox from '@/components/BaseComboBox.vue';
import MobilePage from '@/components/mobile/base/MobilePage.vue';
import MobileCompanyCard from '@/components/mobile/company/MobileCompanyCard.vue';
import MobileCompanyCardSkeleton from '@/components/mobile/company/MobileCompanyCardSkeleton.vue';
import { XMarkIcon } from '@heroicons/vue/20/solid';

// Must be one of the backend's allowed page_size choices (10, 25, 50, 100, 200, 250) in company/filters.py
const PAGE_SIZE = 25
const PAGE_SIZE_CHOICES = [10, 25, 50, 100, 200, 250]

const store = useStore()
const router = useRouter()
const route = useRoute()
const pageRef = ref(null)
const $toast = useToast({ position: 'bottom' })
const token = store.getters['auth/showToken']
const isOnline = useOnline()

const views = [
  { key: 'all', label: 'All Companies' },
  { key: 'saved', label: 'Saved Offline' },
]
const view = ref('all')
const savedCompanies = ref([])
const savedIds = computed(() => new Set(savedCompanies.value.map(company => company.id)))
// Companies whose save or unsave is still in progress
const savingIds = ref(new Set())

const emptyFilters = () => ({ companyName: '', campaign: null, collectionTag: null, source: null })
// Each view keeps its own filters, so switching views doesn't carry them over
const allFilters = reactive(emptyFilters())
const savedFilters = reactive(emptyFilters())
const filters = computed(() => view.value === 'all' ? allFilters : savedFilters)
// Saved filters as of the last Search click
const searchedSavedFilters = ref(emptyFilters())

const campaignOptions = ref([])
const collectionTagOptions = ref([])
const sourceOptions = ref([])

function uniqueOptions(items, labelKey) {
  const options = new Map(items.map(item => [item.id, { value: item.id, label: item[labelKey] }]))
  return [...options.values()].sort((a, b) => a.label.localeCompare(b.label))
}

// Saved view builds its options from the saved companies, so they work offline
const options = computed(() => {
  if (view.value === 'all') {
    return { campaign: campaignOptions.value, collectionTag: collectionTagOptions.value, source: sourceOptions.value }
  }
  return {
    campaign: uniqueOptions(savedCompanies.value.map(company => company.campaign).filter(Boolean), 'campaign_name'),
    collectionTag: uniqueOptions(savedCompanies.value.flatMap(company => company.collection_tags || []), 'collection_tag_name'),
    source: uniqueOptions(savedCompanies.value.flatMap(company => company.sources || []), 'source_name'),
  }
})

const filteredSavedCompanies = computed(() => {
  const { companyName, campaign, collectionTag, source } = searchedSavedFilters.value
  const name = companyName.trim().toLowerCase()
  return savedCompanies.value.filter(company =>
    (!name || company.company_name.toLowerCase().includes(name)) &&
    (!campaign || (company.campaign && company.campaign.id === campaign)) &&
    (!collectionTag || (company.collection_tags || []).some(tag => tag.id === collectionTag)) &&
    (!source || (company.sources || []).some(companySource => companySource.id === source))
  )
})

const companies = ref([])
const page = ref(1)
const hasMore = ref(false)
const numPages = ref(0)
const isLoading = ref(false)
const totalCount = ref(0)
const isCountCapped = ref(false)
const isSavingAll = ref(false)

let latestRequestId = 0
let abortController = null
// Filters as of the last Search click, so Prev/Next page the same results even if the inputs were edited since
let searchedParams = new URLSearchParams()

function buildParams() {
  const params = new URLSearchParams()
  if (allFilters.companyName.trim()) params.append('company_name', allFilters.companyName.trim())
  if (allFilters.campaign) params.append('campaign', allFilters.campaign)
  if (allFilters.collectionTag) params.append('collection_tag', allFilters.collectionTag)
  if (allFilters.source) params.append('source_object', allFilters.source)
  params.append('page_size', PAGE_SIZE)
  return params
}

function search() {
  if (view.value === 'saved') {
    searchedSavedFilters.value = { ...savedFilters }
    return
  }
  searchedParams = buildParams()
  changePage(1)
}

function changePage(pageNumber) {
  if (pageRef.value) pageRef.value.scrollToTop()
  loadCompanies(pageNumber)
}

async function loadCompanies(pageNumber) {
  // Same guard as ConfigurableCompanies.vue: cancel the previous request and ignore stale results
  const requestId = ++latestRequestId
  if (abortController) {
    abortController.abort()
  }
  abortController = new AbortController()

  isLoading.value = true
  const params = new URLSearchParams(searchedParams)
  params.append('page', pageNumber)
  const result = await getPWACompanies({ token, endpoint: `/?${params}`, signal: abortController.signal })

  if (requestId !== latestRequestId) {
    return
  }
  isLoading.value = false
  if (!result) {
    return
  }
  const loadedAt = new Date().toISOString()
  const pageCompanies = result.companies.map(company => ({ ...company, loaded_at: loadedAt }))
  companies.value = pageCompanies
  page.value = pageNumber
  hasMore.value = !!result.next
  numPages.value = result.num_pages
  totalCount.value = result.count
  isCountCapped.value = !!result.count_is_capped
}

// Passes the company in history state so the detail page can show it before its own request finishes
function openCompany(company) {
  router.push({
    name: 'pwa-company-detail',
    params: { companyId: company.id },
    state: { company: JSON.parse(JSON.stringify(company)) },
  })
}

function clearFilters() {
  Object.assign(filters.value, emptyFilters())
}

async function loadFilterOptions() {
  const [campaigns, collectionTags, sources] = await Promise.all([
    getMinimalActiveCampaigns(token),
    getMinimalCollectionTags(token),
    getSources(token),
  ])
  campaignOptions.value = (campaigns || []).map(c => ({ value: c.id, label: c.campaign_name }))
  collectionTagOptions.value = (collectionTags || []).map(t => ({ value: t.id, label: t.collection_tag_name }))
  sourceOptions.value = (sources || []).map(s => ({ value: s.id, label: s.source_name }))
}

async function loadSavedCompanies() {
  savedCompanies.value = await getSavedCompanies()
}

function showSaveError(error) {
  console.log(error)
  $toast.open({ message: "Couldn't update saved companies", type: 'error', duration: 2000 })
}

async function toggleSaved(company) {
  if (savingIds.value.has(company.id)) return
  savingIds.value.add(company.id)
  try {
    if (savedIds.value.has(company.id)) {
      await unsaveCompany(company.id)
    } else if (savedIds.value.size >= MAX_SAVED_COMPANIES) {
      $toast.open({ message: `You can only save up to ${MAX_SAVED_COMPANIES} companies`, type: 'error', duration: 2000 })
      return
    } else if (!(await logSavedCompanies([company.id], token))) {
      $toast.open({ message: NOT_LOGGED_MESSAGE, type: 'error', duration: 2000 })
    } else {
      await saveCompany(company)
    }
  } catch (error) {
    showSaveError(error)
  } finally {
    savingIds.value.delete(company.id)
  }
  await loadSavedCompanies()
}

function companiesText(count) {
  return `${count} ${count === 1 ? 'company' : 'companies'}`
}

// Saves companies matching the last search, across all pages, until the limit is reached
async function saveAll() {
  const remaining = MAX_SAVED_COMPANIES - savedCompanies.value.length
  if (remaining <= 0) {
    $toast.open({ message: `You can only save up to ${MAX_SAVED_COMPANIES} companies`, type: 'error', duration: 2000 })
    return
  }
  const total = isCountCapped.value ? `${totalCount.value}+` : totalCount.value
  if (!confirm(`Save ${Math.min(remaining, totalCount.value)} of ${total} filtered companies?`)) return

  isSavingAll.value = true
  const pageSize = PAGE_SIZE_CHOICES.find(size => size >= remaining) || PAGE_SIZE_CHOICES[PAGE_SIZE_CHOICES.length - 1]
  const toSave = []
  let pageNumber = 1
  let hasNext = true
  let failed = false
  while (hasNext && toSave.length < remaining) {
    const params = new URLSearchParams(searchedParams)
    params.set('page_size', pageSize)
    params.append('page', pageNumber)
    const result = await getPWACompanies({ token, endpoint: `/?${params}` })
    if (!result) {
      failed = true
      break
    }
    toSave.push(...result.companies.filter(company => !savedIds.value.has(company.id)))
    hasNext = !!result.next
    pageNumber++
  }

  const saving = toSave.slice(0, remaining)
  let logged = true
  try {
    if (saving.length > 0) {
      logged = await logSavedCompanies(saving.map(company => company.id), token)
    }
    if (logged) {
      await saveCompanies(saving)
    }
  } catch (error) {
    showSaveError(error)
    return
  } finally {
    await loadSavedCompanies()
    isSavingAll.value = false
  }

  if (!logged) {
    $toast.open({ message: NOT_LOGGED_MESSAGE, type: 'error', duration: 2000 })
    return
  }

  if (failed) {
    $toast.open({ message: `Couldn't load all matching companies. Saved ${companiesText(saving.length)}.`, type: 'error', duration: 2000 })
    return
  }
  const reachedLimit = toSave.length > remaining || (hasNext && saving.length === remaining)
  $toast.open({
    message: `Saved ${companiesText(saving.length)}${reachedLimit ? ` (limit of ${MAX_SAVED_COMPANIES} reached)` : ''}`,
    type: 'success',
    duration: 2000,
  })
}

async function unsaveAll() {
  const ids = filteredSavedCompanies.value.map(company => company.id)
  if (!confirm(`Unsave ${companiesText(ids.length)}?`)) return
  try {
    await unsaveCompanies(ids)
    $toast.open({ message: `Unsaved ${companiesText(ids.length)}`, type: 'success', duration: 2000 })
  } catch (error) {
    showSaveError(error)
  }
  await loadSavedCompanies()
}

// Kept alive, so reload the saved list each time the page is shown
onActivated(loadSavedCompanies)
// The list stays mounted under the company layer, so also reload when the layer closes
// Closing the detail back to this list drops the cached detail, so reopening a company starts fresh
const detailCacheKey = ref(0)
watch(() => route.name, (name, previousName) => {
  if (name === 'pwa-companies' && previousName === 'pwa-company-detail') {
    loadSavedCompanies()
    detailCacheKey.value++
  }
})

loadFilterOptions()
// Show an unfiltered first page when the page opens
search()
</script>
